/**
 * Meeting API Service
 * TASK-393: Meeting Flow Simplification
 *
 * Typed wrapper for meeting-related API calls.
 * Replaces location.state passing with API-first data fetching.
 * All screens fetch their own data by meetingId on mount.
 */

import { httpClient } from '../../lib/httpClient';
import type { PortfolioOverview, Position, RiskMetrics, PerformanceMetrics } from './portfolioApiService';

// ============================================================================
// Types
// ============================================================================

export interface MeetingDetails {
  session_id: string;
  meeting_type: string;
  // 'live' is a real persisted status (start_meeting sets it); include it so the
  // session-scoped MeetingPrep header can render it honestly (TASK-671).
  status: 'scheduled' | 'live' | 'in_progress' | 'post_processing' | 'completed' | 'cancelled';
  client_id: string;
  client_name: string;
  advisor_id: string;
  scheduled_at: string | null;
  started_at: string | null;
  ended_at: string | null;
  duration: number | null;
  // TASK-671: the meeting's own goal, so preparation is bound to THIS meeting and can
  // never be confused with another meeting for the same client.
  meeting_goal?: string | null;
  duration_minutes?: number;
  /**
   * TASK-673: source provenance, carried into REVIEW (deep review #6 — it stopped at the list).
   * 'browser-mic' | 'attendee-bot' | 'mixed' | 'none' | 'unknown'.
   */
  capture_source?: string;
  /** TASK-668: set when the meeting was documented by an approved RM dictation. */
  documentation_source?: string | null;
  documented_at?: string | null;
  preparation_status:
    | 'not_started'
    | 'in_progress'
    | 'completed'
    | 'insight_gathering_in_progress'
    | 'insight_gathering_complete'
    | 'brief_generation_in_progress'
    | 'brief_generated'
    | 'not_applicable';
  language: string;
  has_brief?: boolean;
  client_email?: string;
  // TASK-425/429: optional Teams/Meet URL. Present when the meeting was
  // scheduled with a conference link; consumed by useMeetingCapture to gate the
  // browser-mic MediaRecorder pipeline when an Attendee bot will be the
  // audio source.
  meeting_url?: string;
}

export interface BriefSection {
  section: string;
  content: unknown;
}

/** Context sent from PreparationPage to enrich brief generation with already-loaded data. */
export interface BriefGenerationContext {
  portfolio?: {
    overview: PortfolioOverview | null;
    positions: Position[];
    riskMetrics: RiskMetrics | null;
    performanceMetrics: PerformanceMetrics | null;
  };
  insights?: Array<{ title: string; description?: string; category?: string }>;
  clientProfile?: {
    client_name: string;
    client_id: string;
    meeting_type: string;
  };
}

export interface GenerateBriefResponse {
  success: boolean;
  brief_id: string;
  content: Record<string, unknown>;
}

/**
 * One row of GET /meeting (list). Backend: meeting_routes.py get_meetings().
 * Note "live" is a real persisted status alongside the MeetingDetails ones.
 */
export interface MeetingListItem {
  session_id: string;
  meeting_type: string;
  status: string;
  client_id: string;
  client_name: string;
  advisor_id: string;
  scheduled_at: string | null;
  started_at: string | null;
  ended_at: string | null;
  duration: number | null;
  meeting_goal: string | null;
  duration_minutes: number;
  preparation_status: string;
  has_brief: boolean;
  // TASK-666 — preparation / capture / analysis / documentation are separate states.
  // `capture_source`: 'browser-mic' | 'attendee-bot' | 'mixed' | 'none'
  // DERIVED from `utterances.source` — "which pipeline produced this meeting's words".
  // It reads 'none' for a browser-mic meeting until its first utterance, so it must NOT be
  // used to decide which surface to open. See `capture_mode`.
  capture_source?: string;
  // TASK-700 — the RM's capture INTENT, recorded at start:
  // 'browser-mic' | 'attendee-bot' | 'none'. Undefined means intent was never recorded
  // (pre-migration-200 sessions); readers fall back rather than guessing.
  capture_mode?: string | null;
  // `analysis_state`: not_started | processing | completed | partial_failure | failed
  analysis_state?: string;
  documented_at?: string | null;
  documentation_source?: string | null;
  // TASK-673: presence means a bot owns the audio — the row opens LiveMeetingNow, not the
  // browser recorder, and the Start flow offers no microphone capture path.
  meeting_url?: string | null;
  // TASK-676: whether a recording bot is already attached — gates the "Send bot" action.
  has_bot?: boolean;
}

// ============================================================================
// API Methods
// ============================================================================

/**
 * List the account's meetings (RLS-scoped, newest first, capped at 100).
 * TASK-644. IMPORTANT: the path is /meeting (singular) — gateway /meetings
 * is a removed legacy alias (TASK-648).
 */
export async function listMeetings(): Promise<MeetingListItem[]> {
  return httpClient.get<MeetingListItem[]>('/meeting');
}

// TASK-877: the shared cache key for this fetcher lives in `./queryKeys`, not here —
// see that file for why it cannot be exported from a module the tests mock.


/**
 * Get meeting details by ID.
 * Gateway proxy: /meeting/{path} → orchestrator /meeting/{path}
 * Orchestrator route: meeting_routes.py prefix="/meeting"
 */
export async function getMeetingDetails(meetingId: string): Promise<MeetingDetails> {
  return httpClient.get<MeetingDetails>(`/meeting/${meetingId}/details`);
}

/**
 * TASK-673: transition a scheduled meeting to `live` and record the capture-mode decision.
 * One canonical, RLS-scoped, idempotent start endpoint. `capture_mode` is the RM's explicit
 * choice: 'browser-mic' (in-person recorder), 'attendee-bot' (a bot owns the audio), or
 * 'none' (in person, dictate a summary afterwards — no microphone).
 */
export async function startMeeting(
  meetingId: string,
  captureMode: 'browser-mic' | 'attendee-bot' | 'none',
): Promise<{ status: string; phase: string; capture_mode: string | null }> {
  return httpClient.post(`/meeting/${meetingId}/start`, { capture_mode: captureMode });
}

/**
 * TASK-676: dispatch a recording bot to a meeting that has a conference link. Idempotent;
 * the conference URL stays server-side (resolved from the session). Bot status then flows
 * through the LiveMeetingNow monitor.
 */
export async function dispatchMeetingBot(
  meetingId: string,
): Promise<{ session_id: string; dispatched: boolean }> {
  return httpClient.post(`/meeting/${meetingId}/dispatch-bot`, {});
}

/** TASK-677: cancel a scheduled meeting (idempotent, audited, tears down any bot). */
export async function cancelMeeting(
  meetingId: string,
  reason?: string,
): Promise<{ session_id: string; status: string }> {
  return httpClient.post(`/meeting/${meetingId}/cancel`, { reason });
}

/**
 * TASK-677/674: move a scheduled meeting to a new time. `scheduledAtIso` must be a real UTC
 * instant — compute it with `new Date(`${date}T${time}`).toISOString()` so the RM's local
 * wall-clock is preserved (never a naive time stamped with Z).
 */
export async function rescheduleMeeting(
  meetingId: string,
  scheduledAtIso: string,
): Promise<{ session_id: string; scheduled_at: string }> {
  return httpClient.post(`/meeting/${meetingId}/reschedule`, { scheduled_at: scheduledAtIso });
}

/**
 * Trigger AI brief generation from gathered data.
 * Gateway proxy: /meeting/{path} → orchestrator (wizard_brief_routes prefix="/wizard")
 * The gateway /api/meeting/ proxy is for post_meeting_routes_with_tasks (prefix="/api/meeting").
 * Wizard routes are mounted at /wizard/ so we use the /meeting/ gateway proxy.
 */
export async function generateBrief(
  meetingId: string,
  context?: Record<string, unknown>,
): Promise<GenerateBriefResponse> {
  return httpClient.post<GenerateBriefResponse>(
    `/wizard/${meetingId}/generate-brief`,
    context ? { context } : undefined,
  );
}

/**
 * TASK-413 Bug 3: structured 409 conflict raised when the server detects a
 * stale `expected_version`. Callers (BriefTab Apply handler) should refetch
 * the brief and surface the conflict to the user.
 */
export class BriefVersionConflictError extends Error {
  currentVersion: number;
  section: string;
  constructor(currentVersion: number, section: string) {
    super(`Brief section '${section}' was modified externally (current version: ${currentVersion})`);
    this.name = 'BriefVersionConflictError';
    this.currentVersion = currentVersion;
    this.section = section;
  }
}

/**
 * Update a single section of the brief.
 * Wizard routes mounted at /wizard/
 *
 * TASK-413 Bug 3: optional `expectedVersion` enables optimistic locking. When
 * provided, the server returns 409 if another writer has bumped the version
 * since the caller's last GET. Without it, last-write-wins (deprecated).
 */
export async function updateBriefSection(
  meetingId: string,
  section: string,
  content: unknown,
  expectedVersion?: number,
): Promise<{ success: boolean; version: number }> {
  try {
    return await httpClient.post<{ success: boolean; version: number }>(
      `/wizard/${meetingId}/update-brief-section`,
      {
        section,
        content,
        ...(expectedVersion !== undefined ? { expected_version: expectedVersion } : {}),
      },
    );
  } catch (err: unknown) {
    const resp = (err as { response?: { status?: number; data?: { detail?: { error?: string; current_version?: number; section?: string } } } })?.response;
    if (resp?.status === 409 && resp.data?.detail?.error === 'version_conflict') {
      throw new BriefVersionConflictError(
        resp.data.detail.current_version ?? 0,
        resp.data.detail.section ?? section,
      );
    }
    throw err;
  }
}

/**
 * Get the saved brief for a meeting.
 * Uses the wizard brief endpoint which returns the brief from MeetingSession.preparation_brief.
 * This endpoint returns a proper JSON response (with "error" field) when no brief exists,
 * unlike /api/briefs/ which may 500.
 */
export async function getMeetingBrief(meetingId: string): Promise<Record<string, unknown> | null> {
  try {
    const data = await httpClient.get<Record<string, unknown>>(`/wizard/${meetingId}/brief`);
    // The endpoint returns { error: "No wizard brief created yet" } when empty
    if (data?.error) return null;
    return data;
  } catch (err: unknown) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 404 || status === 500) return null;
    throw err;
  }
}

/**
 * Complete a meeting (mark as finished).
 * Wizard routes mounted at /wizard/
 */
export async function completeMeeting(meetingId: string): Promise<{ success: boolean }> {
  return httpClient.post<{ success: boolean }>(`/wizard/${meetingId}/complete`);
}

// ============================================================================
// Convenience export
// ============================================================================

export const meetingApi = {
  getMeetingDetails,
  generateBrief,
  updateBriefSection,
  getMeetingBrief,
  completeMeeting,
};
